import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '../../components/Layout/Layout';
import { AuroraBackground } from '../../components/Aurora/AuroraBackground';
import { Magnetic } from '../../components/Hero/Magnetic';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Check, Star, ArrowRight, Loader2, Sparkles, Zap, HelpCircle, Eye, MapPin, MessageSquare, Shield, BarChart3, Users, Route, Receipt } from 'lucide-react';
import useCanonical from '../../hooks/useCanonical';

const serifStyle = { fontFamily: '"Instrument Serif"' };

const CARD_FEATURES = [
  'Unlimited coverage areas',
  'Statewide coverage',
  'Priority search ranking',
  'Servio Score™ system',
  'Quote request system',
  'Route Optimizer',
  'Invoice Generator',
  'Stripe payments (no platform fee)',
  'Analytics dashboard',
  'Verification badges',
  'Client reviews',
];

const FREE_FEATURES = [
  'Up to 5 coverage areas',
  'County, City, or ZIP only',
  'Visible in search results',
  'Servio Score™ system',
  'Quote request system',
  'Verification badges',
  'Client reviews',
];

const FAQ_ITEMS = [
  { q: "What's included in the free account?", a: 'Free accounts can list up to 5 coverage areas (county, city, or ZIP code), appear in search results, receive quote requests, and earn verification badges. Statewide coverage, unlimited areas, Route Optimizer, and Invoice Generator require a subscription.' },
  { q: 'How do free vs. paid search rankings work?', a: 'Subscribers appear first in search results, followed by free users. Within each group, servers are ranked by your selected criteria (Servio Score, distance, price, or reviews).' },
  { q: 'What happens after launch pricing ends?', a: 'If you sign up during the launch period (before May 12, 2026), you lock in your rate forever—even if prices increase later. New subscribers after launch pay standard rates of $20.99/month or $209/year.' },
  { q: 'What is the Founding Member badge?', a: 'The first 100 subscribers (monthly or annual) receive an exclusive, permanent "Founding Member" badge displayed on their profile.' },
  { q: 'How does the Servio Score work?', a: 'Your Servio Score is a proprietary trust rating (0-100) calculated from profile completeness, verification status, response time, and client reviews.' },
  { q: 'What is the Route Optimizer?', a: 'Route Optimizer helps you plan efficient service routes. Add stops manually or import from CSV/Excel, set time-window constraints, drag-and-drop to reorder, and navigate using Google Maps, Apple Maps, or Waze. Reset routes for recurring schedules.' },
  { q: 'What is the Invoice Generator?', a: 'Invoice Generator lets you create professional invoices with custom branding (your logo, company name, invoice prefix). Manage clients, set default prices for service types, track payments, add supplemental charges, customize themes, and download PDFs.' },
  { q: 'How do online payments work?', a: "Connect your Stripe account to accept credit card payments directly on your invoices. Clients pay with one click. Payments go directly to your bank account with no platform fees from Servio—you only pay Stripe's standard processing fee (~2.9% + $0.30)." },
  { q: 'Can I upgrade from free to paid anytime?', a: 'Yes! You can upgrade to a subscription at any time to unlock unlimited coverage areas, Route Optimizer, Invoice Generator, and statewide coverage. Your existing areas and profile data are preserved.' },
  { q: 'How do quote requests work?', a: 'Clients submit quote requests with service details. You receive an email notification and can respond with your price or decline.' },
  { q: 'What verification badges can I earn?', a: 'You can earn Licensed, Insured, and Bonded badges by uploading documentation through your dashboard. Both free and paid users can earn badges.' },
  { q: 'Can I cancel anytime?', a: 'Yes. Monthly subscribers can cancel anytime with no penalties. Annual subscribers retain full access until their billing period ends. You\'ll revert to free tier limits after cancellation.' },
  { q: 'Do I need verification to appear in search?', a: 'Yes. All users (free and paid) must complete identity verification before appearing in search results. This ensures only legitimate process servers are listed.' },
];

/* ------- helpers ------- */
function Eyebrow({ children, color = 'hsl(226,100%,52%)', colorDark = 'hsl(226,100%,68%)' }) {
  return (
    <div className="font-mono text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color }}>
      <span className="dark:hidden">◢ {children}</span>
      <span className="hidden dark:inline" style={{ color: colorDark }}>◢ {children}</span>
    </div>
  );
}

function FeatureCategoryCard({ title, features, icon: Icon, accent }) {
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
      className="group relative rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] p-6 hover:border-[hsl(226,100%,55%)]/40 transition-colors overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: accent + '24' }} />
      <div className="relative flex items-start justify-between mb-4">
        <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center">
          <Icon className="h-4 w-4" style={{ color: accent }} strokeWidth={2} />
        </div>
      </div>
      <h3 className="relative text-base font-semibold tracking-tight mb-3">{title}</h3>
      <ul className="relative space-y-2">
        {features.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-[13px] text-slate-600 dark:text-[hsl(220,12%,72%)]">
            <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

function FAQItem({ q, a }) {
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } }}
      className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] p-6 hover:border-[hsl(226,100%,55%)]/40 transition-colors"
    >
      <h3 className="font-semibold tracking-tight mb-2 text-[15px]">{q}</h3>
      <p className="text-sm text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">{a}</p>
    </motion.div>
  );
}

function PricingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [foundingData, setFoundingData] = useState({ remaining: 100, total: 100 });
  const [loading, setLoading] = useState(null);

  useCanonical('https://servio.legal/pricing');

  useEffect(() => {
    api.getFoundingCount().then(r => setFoundingData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [location.hash]);

  const handleSubscribe = async (planType) => {
    if (!isAuthenticated) {
      toast.info('Please create an account first');
      navigate('/register');
      return;
    }
    setLoading(planType);
    try {
      const r = await api.createCheckout(planType);
      window.location.href = r.data.url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(i => ({
      '@type': 'Question',
      name: i.q,
      acceptedAnswer: { '@type': 'Answer', text: i.a },
    })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[hsl(220,40%,5%)] text-slate-900 dark:text-[hsl(220,10%,96%)]">
      <Helmet>
        <title>Pricing | Servio · Process Server Directory</title>
        <meta name="description" content="Join Servio with launch pricing. Free tier available. Pro subscribers get unlimited coverage areas, priority ranking, and exclusive badges. Lock in your rate forever." />
        <link rel="canonical" href="https://servio.legal/pricing" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <Navbar />

      <main className="flex-1" data-testid="pricing-page">

        {/* ════════════════ HERO ════════════════ */}
        <section className="relative overflow-hidden isolate" data-testid="pricing-hero">
          <div className="hidden dark:block"><AuroraBackground intensity={0.85} /></div>
          <div className="absolute inset-0 dark:hidden pointer-events-none" style={{
            background:
              'radial-gradient(800px 400px at 20% 0%, hsl(226 100% 55% / 0.08), transparent 60%),' +
              'radial-gradient(900px 500px at 95% 10%, hsl(270 95% 60% / 0.06), transparent 60%),' +
              'radial-gradient(700px 500px at 50% 100%, hsl(150 80% 55% / 0.05), transparent 60%)',
          }} />
          <div className="absolute inset-0 dark:hidden opacity-[0.5] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(hsl(220 16% 92%) 1px, transparent 1px), linear-gradient(90deg, hsl(220 16% 92%) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent 75%)',
          }} />

          <div className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 pt-16 pb-12 lg:pt-20 lg:pb-16 text-center">
            {foundingData.remaining > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                className="flex justify-center mb-8"
              >
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 dark:bg-white/[0.06] backdrop-blur-md border border-slate-200 dark:border-white/10 text-xs font-medium" data-testid="founding-banner">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(150,80%,55%)] opacity-70 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(150,80%,55%)]" />
                  </span>
                  <span className="text-slate-600 dark:text-[hsl(220,12%,80%)]">Founding pricing live ·</span>
                  <span className="font-mono tabular-nums text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] font-bold">{foundingData.remaining}</span>
                  <span className="text-slate-600 dark:text-[hsl(220,12%,80%)]">seats left</span>
                </span>
              </motion.div>
            )}

            <motion.h1
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-bold leading-[0.95] tracking-[-0.045em] mx-auto max-w-4xl"
              style={{ fontSize: 'clamp(2.2rem, 5.6vw, 4.6rem)' }}
            >
              <span className="block">Start free.</span>
              <span className="block">
                <span>Grow </span>
                <span className="italic font-normal text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] dark:glow-cobalt" style={serifStyle}>unlimited.</span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-6 max-w-xl mx-auto text-base sm:text-lg leading-relaxed text-slate-600 dark:text-[hsl(220,12%,72%)]"
            >
              List for free with up to 5 coverage areas. Subscribe for unlimited areas, statewide coverage, and priority placement.
            </motion.p>
          </div>
        </section>

        {/* ════════════════ PLANS ════════════════ */}
        <section id="plans" className="relative border-t border-slate-200 dark:border-white/[0.06] py-16 lg:py-20 scroll-mt-24">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <Eyebrow>PLANS</Eyebrow>
                <h2 className="font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
                  Three tiers. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>Zero surprises.</span>
                </h2>
              </div>
              <p className="max-w-md text-sm text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">
                Founding seats lock in launch pricing for life. After 100 sign-ups, regular pricing kicks in.
              </p>
            </div>

            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: '0px 0px -80px 0px' }}
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
              className="grid md:grid-cols-3 gap-4 max-w-6xl mx-auto overflow-visible"
            >
              {/* FREE */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                className="relative rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] p-7 lg:p-8 hover:border-slate-300 dark:hover:border-white/15 transition-colors"
                data-testid="free-plan"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4 text-slate-500 dark:text-[hsl(220,12%,60%)]" />
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 dark:text-[hsl(220,12%,60%)]">Free</span>
                </div>
                <div className="flex items-baseline gap-2 mt-3">
                  <span className="font-mono font-bold tracking-tight" style={{ fontSize: 'clamp(2.4rem, 4vw, 3.2rem)' }}>$0</span>
                  <span className="text-slate-500 dark:text-[hsl(220,12%,60%)]">/forever</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-[hsl(220,12%,60%)] mt-1">Get started today.</p>

                <ul className="mt-8 space-y-2.5">
                  {FREE_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] text-slate-600 dark:text-[hsl(220,12%,72%)]">
                      <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-slate-500 dark:text-[hsl(220,12%,60%)]" />
                      </div>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/register" className="mt-8 inline-flex w-full items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 bg-white dark:bg-white/[0.04] font-semibold tracking-tight text-sm transition-colors" data-testid="start-free">
                  Start Free <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>

              {/* MONTHLY */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                className="relative rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[hsl(220,30%,8%)] p-7 lg:p-8 hover:border-[hsl(226,100%,55%)]/40 transition-colors"
                data-testid="monthly-plan"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-4 w-4 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" />
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]">Monthly</span>
                </div>
                <div className="flex items-baseline gap-2 mt-3">
                  <span className="font-mono font-bold tracking-tight" style={{ fontSize: 'clamp(2.4rem, 4vw, 3.2rem)' }}>$13.99</span>
                  <span className="text-slate-500 dark:text-[hsl(220,12%,60%)]">/mo</span>
                </div>
                <p className="text-sm mt-1">
                  <span className="line-through text-slate-400 dark:text-[hsl(220,12%,45%)]">$20.99</span>
                  <span className="ml-2 text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] font-medium">Launch rate · locked for life</span>
                </p>

                <ul className="mt-8 space-y-2.5">
                  {CARD_FEATURES.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13px] text-slate-600 dark:text-[hsl(220,12%,75%)]">
                      <div className="w-5 h-5 rounded-full bg-[hsl(226,100%,55%)]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" />
                      </div>
                      <span>{f}</span>
                    </li>
                  ))}
                  {foundingData.remaining > 0 && (
                    <li className="flex items-start gap-3 text-[13px] font-medium text-[hsl(38,95%,45%)] dark:text-[hsl(38,95%,60%)]">
                      <div className="w-5 h-5 rounded-full bg-[hsl(38,95%,55%)]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Star className="h-3 w-3 fill-current" />
                      </div>
                      Founding Member badge
                    </li>
                  )}
                </ul>

                <button onClick={() => handleSubscribe('monthly')} disabled={loading === 'monthly'} className="mt-8 inline-flex w-full items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 dark:border-white/10 hover:border-[hsl(226,100%,55%)]/40 bg-white dark:bg-white/[0.04] font-semibold tracking-tight text-sm transition-colors disabled:opacity-60" data-testid="subscribe-monthly">
                  {loading === 'monthly' ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start Monthly <ArrowRight className="h-4 w-4" /></>}
                </button>
              </motion.div>

              {/* ANNUAL — highlighted */}
              <motion.div
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                className="relative mt-6 md:mt-0 overflow-visible"
                data-testid="annual-plan"
              >
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[hsl(226,100%,55%)] text-white text-[10px] font-mono tracking-[0.18em] uppercase font-bold shadow-[0_8px_24px_-8px_hsl(226,100%,55%)]">
                    <Sparkles className="h-3 w-3" />
                    Best value
                  </div>
                </div>

                <div className="relative rounded-3xl border-2 border-[hsl(226,100%,55%)]/50 bg-white dark:bg-[hsl(220,30%,8%)] p-7 lg:p-8 shadow-[0_24px_60px_-16px_hsl(226,100%,55%)/0.3]">
                  <div className="absolute inset-0 rounded-3xl pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 0%, hsl(226 100% 55% / 0.08), transparent 70%)' }} />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="h-4 w-4 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] fill-current" />
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]">Annual</span>
                    </div>
                    <div className="flex items-baseline gap-2 mt-3">
                      <span className="font-mono font-bold tracking-tight" style={{ fontSize: 'clamp(2.4rem, 4vw, 3.2rem)' }}>$139</span>
                      <span className="text-slate-500 dark:text-[hsl(220,12%,60%)]">/yr</span>
                    </div>
                    <p className="text-sm mt-1">
                      <span className="line-through text-slate-400 dark:text-[hsl(220,12%,45%)]">$209</span>
                      <span className="ml-2 text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] font-medium">Save $28 vs monthly</span>
                    </p>

                    <ul className="mt-8 space-y-2.5">
                      {CARD_FEATURES.map((f, i) => (
                        <li key={i} className="flex items-start gap-3 text-[13px] text-slate-600 dark:text-[hsl(220,12%,75%)]">
                          <div className="w-5 h-5 rounded-full bg-[hsl(226,100%,55%)]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="h-3 w-3 text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)]" />
                          </div>
                          <span>{f}</span>
                        </li>
                      ))}
                      {foundingData.remaining > 0 && (
                        <li className="flex items-start gap-3 text-[13px] font-medium text-[hsl(38,95%,45%)] dark:text-[hsl(38,95%,60%)]">
                          <div className="w-5 h-5 rounded-full bg-[hsl(38,95%,55%)]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Star className="h-3 w-3 fill-current" />
                          </div>
                          Founding Member badge
                        </li>
                      )}
                    </ul>

                    <Magnetic strength={8} block>
                      <button onClick={() => handleSubscribe('annual')} disabled={loading === 'annual'} className="mt-8 inline-flex w-full items-center justify-center gap-2 h-11 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] text-white font-semibold tracking-tight text-sm shadow-[0_12px_36px_-10px_hsl(226,100%,55%)] transition-colors disabled:opacity-60" data-testid="subscribe-annual">
                        {loading === 'annual' ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Start Annual & Save <ArrowRight className="h-4 w-4" /></>}
                      </button>
                    </Magnetic>

                    {foundingData.remaining > 0 && (
                      <p className="text-center text-[11px] font-mono tracking-[0.18em] uppercase text-slate-500 dark:text-[hsl(220,12%,60%)] mt-4">
                        {foundingData.remaining}/{foundingData.total} Founding seats
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ════════════════ FEATURES ════════════════ */}
        <section id="features" className="relative border-t border-slate-200 dark:border-white/[0.06] py-16 lg:py-20 scroll-mt-24">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <Eyebrow color="hsl(38,95%,45%)" colorDark="hsl(38,95%,60%)">FEATURES</Eyebrow>
                <h2 className="font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
                  Every tool. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>Built in.</span>
                </h2>
              </div>
              <p className="max-w-md text-sm text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">
                The full toolkit — profile, coverage, quotes, routes, invoices, trust signals, analytics. No add-ons. No surprises.
              </p>
            </div>

            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: '0px 0px -80px 0px' }}
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
              className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3"
            >
              <FeatureCategoryCard title="Profile & Visibility" features={['Pro public profile page','Custom shareable URL','Photo, bio, coverage map','Business address listing']} icon={Eye} accent="hsl(226,100%,55%)" />
              <FeatureCategoryCard title="Coverage & Pricing"   features={['Unlimited state coverage','County / city / ZIP','Custom rates per area','Rush & same-day fees']} icon={MapPin} accent="hsl(190,95%,50%)" />
              <FeatureCategoryCard title="Quote Management"     features={['Instant quote inbox','Email notifications','Accept / decline flow','Response time tracking']} icon={MessageSquare} accent="hsl(270,80%,60%)" />
              <FeatureCategoryCard title="Route Optimizer"      features={['CSV / Excel import','Time-window constraints','Drag-and-drop reorder','Multi-app navigation']} icon={Route} accent="hsl(220,90%,60%)" />
              <FeatureCategoryCard title="Invoice Generator"    features={['Custom branding & themes','Stripe payments built-in','No platform fees','Client management']} icon={Receipt} accent="hsl(150,80%,40%)" />
              <FeatureCategoryCard title="Trust & Verification" features={['Servio Score™ rating','Licensed badge','Insured badge','Bonded badge']} icon={Shield} accent="hsl(38,95%,50%)" />
              <FeatureCategoryCard title="Analytics & Insights" features={['Quote activity feed','Profile view tracking','Origin map','Performance metrics']} icon={BarChart3} accent="hsl(340,90%,55%)" />
              <FeatureCategoryCard title="Founding Perks"       features={['Permanent badge','Locked launch pricing','Priority support','First access to features']} icon={Sparkles} accent="hsl(320,90%,55%)" />
            </motion.div>
          </div>
        </section>

        {/* ════════════════ FAQ ════════════════ */}
        <section id="faq" className="relative border-t border-slate-200 dark:border-white/[0.06] py-16 lg:py-20 scroll-mt-24">
          <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <Eyebrow color="hsl(150,80%,40%)" colorDark="hsl(150,80%,60%)">FAQ</Eyebrow>
                <h2 className="font-bold tracking-[-0.035em] leading-[0.95]" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.8rem)' }}>
                  Got questions? <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>Real answers.</span>
                </h2>
              </div>
              <div className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-[hsl(220,12%,72%)]">
                <HelpCircle className="h-4 w-4" />
                Still curious? <Link to="/contact" className="font-semibold text-[hsl(226,100%,52%)] dark:text-[hsl(226,100%,68%)] hover:underline">Ask us anything</Link>
              </div>
            </div>

            <motion.div
              initial="hidden" whileInView="show" viewport={{ once: true, margin: '0px 0px -80px 0px' }}
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.04 } } }}
              className="grid md:grid-cols-2 gap-3 max-w-6xl mx-auto"
            >
              {FAQ_ITEMS.map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} />
              ))}
            </motion.div>
          </div>
        </section>

        {/* ════════════════ CTA ════════════════ */}
        <section className="relative border-t border-slate-200 dark:border-white/[0.06] py-20 lg:py-28 overflow-hidden" data-testid="cta-section">
          <div className="hidden dark:block"><AuroraBackground intensity={0.7} /></div>
          <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 text-center">
            <Eyebrow color="hsl(150,80%,40%)" colorDark="hsl(150,80%,60%)">FOUNDING SEATS</Eyebrow>
            <h2 className="font-bold tracking-[-0.04em] leading-[0.95] max-w-4xl mx-auto" style={{ fontSize: 'clamp(2rem, 4.4vw, 3.6rem)' }}>
              Get listed. <span className="italic font-normal text-slate-500 dark:text-[hsl(220,12%,60%)]" style={serifStyle}>Get found.</span>{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, hsl(226,100%,52%), hsl(270,95%,60%), hsl(320,90%,55%))' }}>
                Get paid.
              </span>
            </h2>
            <p className="mt-5 max-w-2xl mx-auto text-base text-slate-600 dark:text-[hsl(220,12%,72%)] leading-relaxed">
              The first <span className="font-mono font-bold text-slate-900 dark:text-white">100</span> servers lock in founding pricing for life — <span className="font-mono font-bold text-slate-900 dark:text-white">$13.99</span>/mo or <span className="font-mono font-bold text-slate-900 dark:text-white">$139</span>/yr — unlimited coverage, statewide visibility, full toolkit.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Magnetic strength={14}>
                <Link to="/register" className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-xl bg-[hsl(226,100%,55%)] hover:bg-[hsl(226,100%,62%)] text-white font-semibold tracking-tight text-sm shadow-[0_12px_36px_-10px_hsl(226,100%,55%)] transition-colors" data-testid="cta-join">
                  Claim Founding Seat <ArrowRight className="h-4 w-4" />
                </Link>
              </Magnetic>
              <Link to="/search" className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-xl border border-slate-300 dark:border-white/15 hover:border-slate-400 dark:hover:border-white/30 bg-white dark:bg-white/5 text-slate-900 dark:text-white font-semibold tracking-tight text-sm transition-colors">
                Browse Servers
              </Link>
            </div>
            {foundingData.remaining > 0 && (
              <div className="mt-8 inline-flex items-center gap-3 text-sm text-slate-500 dark:text-[hsl(220,12%,60%)]">
                <span className="font-mono font-bold tabular-nums text-[hsl(150,80%,40%)] dark:text-[hsl(150,80%,60%)] text-base">{foundingData.remaining}</span>
                <span>of 100 seats remaining · Locked for life</span>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default PricingPage;
